import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import vetLogo from "@/assets/vet-logo.webp";
import { useAuth } from "@/contexts/AuthContext";

export default function TransitionScreen() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [stage, setStage] = useState(0);

  // useMemo to avoid recalculating random values on every render
  const particleConfigs = useMemo(() => {
    /* eslint-disable react-hooks/purity */
    return Array.from({ length: 20 }, () => ({
      initialX: Math.random() * window.innerWidth,
      initialY: Math.random() * window.innerHeight,
      animateY: Math.random() * window.innerHeight,
      animateX: Math.random() * window.innerWidth,
      duration: 3 + Math.random() * 2,
      delay: Math.random() * 2,
    }));
    /* eslint-enable react-hooks/purity */
  }, []);

  useEffect(() => {
    // Wait until the auth query has settled before deciding what to do.
    // Without this guard, navigating here right after login causes a false
    // !isAuthenticated while the current-user query is still resolving.
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    const timers = [
      setTimeout(() => setStage(1), 500),
      setTimeout(() => setStage(2), 1200),
      setTimeout(() => setStage(3), 2400),
      setTimeout(() => navigate("/dashboard/registrations", { replace: true }), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, [navigate, isAuthenticated, isLoading]);

  if (stage >= 3) {
    return null;
  }

  return (
    <motion.div
      key="transition"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-blue-600/95 via-cyan-600/95 to-green-500/95 backdrop-blur-2xl">
      {/* Enhanced Pulsing rings with gradient colors */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0, opacity: 0.4 }}
            animate={{ scale: [0, 4.5], opacity: [0.4, 0] }}
            transition={{
              duration: 3,
              delay: i * 0.4,
              repeat: Infinity,
              ease: "easeOut",
            }}
            className={`absolute rounded-full border-2 ${
              i % 3 === 0
                ? "border-blue-400/40"
                : i % 3 === 1
                  ? "border-cyan-400/40"
                  : "border-green-400/40"
            }`}
            style={{
              width: "200px",
              height: "200px",
              boxShadow:
                i % 2 === 0
                  ? "0 0 30px rgba(59, 130, 246, 0.3)"
                  : "0 0 30px rgba(34, 211, 238, 0.3)",
            }}
          />
        ))}
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particleConfigs.map((config, i) => (
          <motion.div
            key={`particle-${i}`}
            initial={{
              x: config.initialX,
              y: config.initialY,
              scale: 0,
              opacity: 0,
            }}
            animate={{
              y: [null, config.animateY],
              x: [null, config.animateX],
              scale: [0, 1, 0],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: config.duration,
              repeat: Infinity,
              delay: config.delay,
              ease: "easeInOut",
            }}
            className="absolute h-2 w-2 rounded-full bg-white/40"
          />
        ))}
      </div>

      {/* Centered card with enhanced animations */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0, rotateY: -180 }}
        animate={{ scale: 1, opacity: 1, rotateY: 0 }}
        transition={{
          type: "spring",
          stiffness: 150,
          damping: 18,
          duration: 0.8,
        }}
        className="relative z-10 flex flex-col items-center gap-8 rounded-[2.5rem] border-4 border-white/20 bg-white p-14 shadow-[0_20px_80px_-15px_rgba(0,0,0,0.5)]">
        {/* Logo with enhanced animation */}
        <motion.div
          initial={{ scale: 0, rotate: -360, opacity: 0 }}
          animate={{
            scale: [0, 1.2, 1],
            rotate: [-360, 20, 0],
            opacity: 1,
          }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            duration: 1,
          }}
          className="relative">
          {/* Glow effect behind logo */}
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-blue-500 to-green-500 blur-2xl"
          />

          <img
            src={vetLogo}
            alt="VetAssist Logo"
            className="h-32 w-32 rounded-3xl object-cover shadow-2xl ring-4 ring-white/50"
          />

          {/* Sparkle effects around logo */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={`sparkle-${i}`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 1.5,
                delay: 0.3 + i * 0.2,
                repeat: Infinity,
                repeatDelay: 1,
              }}
              className="absolute"
              style={{
                top: i === 0 ? "-10px" : i === 1 ? "-10px" : "auto",
                bottom: i === 2 ? "-10px" : i === 3 ? "-10px" : "auto",
                left: i === 0 || i === 2 ? "-10px" : "auto",
                right: i === 1 || i === 3 ? "-10px" : "auto",
              }}>
              <Sparkles className="h-6 w-6 text-yellow-400" />
            </motion.div>
          ))}
        </motion.div>

        {/* Checkmark with bigger animation */}
        <AnimatePresence>
          {stage >= 1 && (
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{
                scale: [0, 1.3, 1],
                rotate: [-180, 10, 0],
              }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 250,
                damping: 18,
              }}>
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}>
                <CheckCircle2
                  className="h-20 w-20 text-green-500 drop-shadow-lg"
                  strokeWidth={2.5}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Text with enhanced animations */}
        <AnimatePresence>
          {stage >= 2 && (
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -40, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              className="text-center">
              <motion.h2
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="bg-gradient-to-r from-blue-600 via-cyan-600 to-green-600 bg-clip-text text-4xl font-black text-transparent">
                Login Successful!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-muted-foreground mt-3 text-lg font-medium">
                Redirecting to dashboard…
              </motion.p>

              {/* Enhanced progress bar */}
              <div className="bg-muted mt-6 h-2 w-64 overflow-hidden rounded-full shadow-inner">
                <motion.div
                  initial={{ width: "0%", x: "-100%" }}
                  animate={{ width: "100%", x: "0%" }}
                  transition={{ duration: 1, ease: "easeInOut" }}
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 shadow-lg">
                  <motion.div
                    animate={{
                      x: ["-100%", "100%"],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="h-full w-1/3 bg-white/30"
                  />
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
